import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const PrivacyPage = () => {
  return (
    <div data-testid="privacy-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-16 noise-bg">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-slate-300">Last updated: December 2025</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-light">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction</h2>
              <p className="text-slate-600 mb-6">
                CyberX Calls ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our VoIP services.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Information We Collect</h2>
              <p className="text-slate-600 mb-4">We may collect the following types of information:</p>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-3">2.1 Personal Information</h3>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li>Name and contact information (email, phone number)</li>
                <li>Company name and business details</li>
                <li>Billing and payment information</li>
                <li>Account credentials</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">2.2 Usage Information</h3>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Call detail records (CDRs) - numbers called, duration, timestamps</li>
                <li>IP addresses and connection data</li>
                <li>Service usage patterns</li>
                <li>Technical logs for troubleshooting</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-slate-600 mb-4">We use collected information to:</p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Provide and maintain our VoIP services</li>
                <li>Process payments and manage your account</li>
                <li>Send service-related communications</li>
                <li>Improve our services and customer experience</li>
                <li>Comply with legal obligations</li>
                <li>Detect and prevent fraud or abuse</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Information Sharing</h2>
              <p className="text-slate-600 mb-4">We may share your information with:</p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li><strong>Carriers and Partners:</strong> To route and complete calls</li>
                <li><strong>Payment Processors:</strong> To process transactions</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
                <li><strong>Service Providers:</strong> Who assist in operating our business</li>
              </ul>
              <p className="text-slate-600 mb-6">
                We do not sell your personal information to third parties.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data Security</h2>
              <p className="text-slate-600 mb-6">
                We implement appropriate technical and organizational measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Encryption of sensitive data</li>
                <li>Secure access controls</li>
                <li>Regular security assessments</li>
                <li>Employee training on data protection</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data Retention</h2>
              <p className="text-slate-600 mb-6">
                We retain your information for as long as your account is active or as needed to provide services. 
                Call detail records are retained for billing purposes and may be kept for regulatory compliance.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Your Rights</h2>
              <p className="text-slate-600 mb-4">Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to certain processing activities</li>
                <li>Data portability</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Cookies and Tracking</h2>
              <p className="text-slate-600 mb-6">
                Our website may use cookies and similar technologies to enhance user experience and analyze usage. 
                You can control cookie settings through your browser preferences.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. International Transfers</h2>
              <p className="text-slate-600 mb-6">
                As a global service, your information may be transferred to and processed in countries other than 
                your own. We ensure appropriate safeguards are in place for such transfers.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Children's Privacy</h2>
              <p className="text-slate-600 mb-6">
                Our services are not intended for individuals under 18 years of age. We do not knowingly collect 
                information from children.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Changes to This Policy</h2>
              <p className="text-slate-600 mb-6">
                We may update this Privacy Policy from time to time. We will notify you of significant changes 
                through our website or email.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Contact Us</h2>
              <p className="text-slate-600 mb-6">
                For privacy-related inquiries, contact us at:
              </p>
              <ul className="list-none text-slate-600 mb-6 space-y-2">
                <li><strong>Email:</strong> admin@cyberxcalls.com</li>
                <li><strong>Phone:</strong> +1 (973) 922-3220</li>
                <li><strong>Telegram:</strong> @cyberx007</li>
              </ul>

              <div className="border-t border-slate-200 pt-6 mt-8">
                <p className="text-slate-500 text-sm">
                  By using CyberX Calls services, you consent to this Privacy Policy and our collection and 
                  use of your information as described herein.
                </p>
              </div>
            </div>

            <div className="text-center mt-8">
              <Link to="/terms" className="text-red-600 hover:text-red-700 font-medium">
                View Terms of Service →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
